// Cliente real de la pantalla Clientes del Admin VL. Los tres endpoints viven en
// `retail-wizard-business` y devuelven un shape distinto al de la pantalla: van por adaptador.
import { apiFetch } from '../../../client';
import type { SaleLine } from '../../retail-wizard/contract';
import { lineNetCents, soldCentsOf } from '../../retail-wizard/soldAmount';
import { toLineProduct, type LineProductWire } from '../../retail-wizard/sessionMapper';
import type {
    BackofficeCustomerDetail,
    BackofficeCustomerPurchase,
    BackofficeCustomerPurchasesPage,
    BackofficeCustomerRow,
    BackofficeCustomersResponse,
    CustomersBackofficeApi,
    ListCustomersOptions,
    PaymentKind,
    ProductCategory,
    PurchaseLine,
} from './contract';

const BASE_URL = import.meta.env.VITE_API_RETAIL_WIZARD_BASE_URL;

const CATEGORIES: readonly ProductCategory[] = ['PHONE', 'SIM', 'MODEM', 'ACCESSORY', 'BUNDLE'];

/** `RetailCustomerListItem` del silo. */
interface SiloCustomerListItem {
    retailCustomerId: string;
    customerName: string | null;
    customerPhone: string | null;
    salesCount: number | null;
    totalSpentCents: number | null;
    firstSaleAt: string | null;
    lastSaleAt: string | null;
    lastStoreId: string | null;
    lastStoreName: string | null;
    lastSellerId: string | null;
    lastSellerName: string | null;
}

interface SiloCustomerDetail extends SiloCustomerListItem {
    aggregatesUnavailableReason?: string | null;
}

type SiloLine = Pick<SaleLine, 'sku' | 'qty' | 'basePriceCents' | 'discountCents' | 'role'> &
    Partial<Pick<SaleLine, 'lineId' | 'paymentType'>> & {
        product: Partial<LineProductWire> | null;
    };

/** La venta del silo, recortada a lo que la pantalla lee. */
interface SiloSale {
    saleId: string;
    folio: string | null;
    soldAt: string;
    status: string | null;
    sku: string | null;
    productBrand: string | null;
    productModel: string | null;
    storeId: string | null;
    storeName: string | null;
    sellerRetailUserId: string | null;
    sellerName: string | null;
    type: string | null;
    externalProvider: string | null;
    amountCents: number | null;
    soldCents?: number | null;
    equipmentPriceCents?: number | null;
    lines?: SiloLine[];
}

interface SiloListResponse {
    items: SiloCustomerListItem[];
    count: number;
    truncated: boolean;
    from?: string | null;
    to?: string | null;
    aggregatesUnavailableReason?: string | null;
}

interface SiloPurchasesResponse {
    items: SiloSale[];
    nextToken?: string;
}

function queryOf(params: Record<string, string | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
    const query = search.toString();
    return query ? `?${query}` : '';
}

function averageTicket(totalCents: number | null, count: number | null): number | null {
    if (totalCents === null || count === null || count === 0) return null;
    return Math.round(totalCents / count);
}

function categoryOf(raw: string | null | undefined): ProductCategory | null {
    return CATEGORIES.includes(raw as ProductCategory) ? (raw as ProductCategory) : null;
}

/** Sin `externalProvider` la venta a crédito es de SureKeep; el resto es contado. */
function paymentOf(type: string | null, externalProvider: string | null): PaymentKind {
    if (externalProvider) return externalProvider;
    return type === 'CREDIT' ? 'SUREKEEP' : 'CASH';
}

function adaptRow(item: SiloCustomerListItem): BackofficeCustomerRow {
    return {
        retailCustomerId: item.retailCustomerId,
        name: item.customerName,
        phone: item.customerPhone,
        purchaseCount: item.salesCount,
        totalSpentCents: item.totalSpentCents,
        averageTicketCents: averageTicket(item.totalSpentCents, item.salesCount),
        lastPurchaseAt: item.lastSaleAt,
        lastStoreId: item.lastStoreId,
        lastStoreName: item.lastStoreName,
        lastSellerId: item.lastSellerId,
        lastSellerName: item.lastSellerName,
    };
}

function adaptLine(line: SiloLine): PurchaseLine {
    const category = categoryOf(line.product?.category);
    return {
        sku: line.sku,
        category,
        qty: line.qty,
        amountCents: lineNetCents(line),
        basePriceCents: line.basePriceCents,
        ...(line.discountCents !== undefined && { discountCents: line.discountCents }),
        ...(line.lineId && { lineId: line.lineId }),
        ...(line.product &&
            category && {
                product: toLineProduct({
                    ...line.product,
                    category,
                    trackingMode: line.product.trackingMode ?? 'QUANTITY',
                }),
            }),
        ...(line.role && { role: line.role }),
        ...(line.paymentType && { paymentType: line.paymentType }),
    };
}

function adaptPurchase(sale: SiloSale): BackofficeCustomerPurchase {
    const productName = [sale.productBrand, sale.productModel].filter(Boolean).join(' ');
    const lines = (sale.lines ?? []).map(adaptLine);
    const main = lines.find((l) => l.sku === sale.sku) ?? lines.at(0);
    return {
        saleId: sale.saleId,
        folio: sale.folio,
        soldAt: sale.soldAt,
        sku: sale.sku?.trim() ? sale.sku : null,
        productName: productName || (sale.sku ?? ''),
        category: main?.category ?? null,
        storeId: sale.storeId,
        storeName: sale.storeName,
        sellerId: sale.sellerRetailUserId,
        sellerName: sale.sellerName,
        payment: paymentOf(sale.type, sale.externalProvider),
        // En crédito `amountCents` es solo el enganche; lo vendido es el precio completo.
        amountCents: soldCentsOf({
            soldCents: sale.soldCents,
            equipmentPriceCents: sale.equipmentPriceCents,
            amountCents: sale.amountCents ?? 0,
        }),
        lines,
    };
}

export const customersBackofficeClient: CustomersBackofficeApi = {
    async listCustomers(options?: ListCustomersOptions): Promise<BackofficeCustomersResponse> {
        const silo = await apiFetch<SiloListResponse>(
            `/backoffice/customers${queryOf({ value: options?.retailerId, from: options?.from })}`,
            { baseUrl: BASE_URL },
        );
        return {
            items: silo.items.map(adaptRow),
            totalCount: silo.count,
            windowFrom: silo.from ?? null,
            windowTo: silo.to ?? null,
            truncated: silo.truncated,
            aggregatesUnavailableReason: silo.aggregatesUnavailableReason ?? null,
        };
    },

    async getCustomer(
        retailCustomerId: string,
        retailerId?: string,
    ): Promise<BackofficeCustomerDetail> {
        const silo = await apiFetch<SiloCustomerDetail>(
            `/backoffice/customers/${encodeURIComponent(retailCustomerId)}${queryOf({ value: retailerId })}`,
            { baseUrl: BASE_URL },
        );
        return {
            ...adaptRow(silo),
            email: null,
            firstPurchaseAt: silo.firstSaleAt,
            aggregatesUnavailableReason: silo.aggregatesUnavailableReason ?? null,
        };
    },

    async listPurchases(
        retailCustomerId: string,
        options?: { nextToken?: string; retailerId?: string },
    ): Promise<BackofficeCustomerPurchasesPage> {
        const query = queryOf({ value: options?.retailerId, nextToken: options?.nextToken });
        const silo = await apiFetch<SiloPurchasesResponse>(
            `/backoffice/customers/${encodeURIComponent(retailCustomerId)}/purchases${query}`,
            { baseUrl: BASE_URL },
        );
        // Una venta cancelada no es una compra: no entra al historial ni a los totales.
        const items = silo.items.filter((sale) => sale.status !== 'CANCELLED').map(adaptPurchase);
        return { items, nextToken: silo.nextToken ?? null };
    },
};
